import React from "react";
import { getUserFacingError } from "../../utils/userFacingError";
import { useLocation, useParams } from "react-router-dom";
import { BannerFeedback } from "../../components/BannerFeedback";
import "./invite.css";
import { Send } from "@mui/icons-material";
import { enterpriseAPI } from "../..";
import { Button } from "../../components/Button";
import { Avatar } from "@mui/material";
import { getBandwidthMbps } from "../../utils/utils";
import { replaceEnterpriseAuthSession } from "../../auth/auth";
import {
  cleanOwnerInviteLocation,
  readOwnerInviteToken,
} from "../../services/accessRequestOnboarding";

type OrgProps = {
  inviteId: string;
  isOwner: boolean;
  collaboratorEmail: string;
  orgName: string;
  orgAvatar: string;
  representativeName?: string;
  representativePhone?: string;
};

const INVITE_REQUEST_TIMEOUT_MS = 12000;

export const InvitePage: React.FC = () => {
  const { inviteID } = useParams();
  const location = useLocation();
  // Router navigation creates a new form, including same-ID replacement links.
  // Cleaning the capability with history.replaceState does not change this key.
  return (
    <InviteSession
      key={`${inviteID}:${location.key}`}
      inviteID={inviteID ?? ""}
      initialToken={readOwnerInviteToken(location.hash, location.search)}
    />
  );
};

const InviteSession: React.FC<{ inviteID: string; initialToken: string }> = ({
  inviteID,
  initialToken,
}) => {
  const [inviteToken, setInviteToken] = React.useState<string>("");
  const [manualToken, setManualToken] = React.useState<string>("");
  const [orgData, setOrgData] = React.useState<OrgProps>();
  const [bannerFeedbackIsOpen, setBannerFeedbackIsOpen] = React.useState(false);
  const [bannerFeedbackType, setBannerFeedbackType] = React.useState<
    "error" | "success" | "warning" | "info"
  >("error");
  const [bannerFeedbackMessage, setBannerFeedbackMessage] = React.useState("");
  const initialInviteToken = React.useRef(initialToken.trim());
  const retryInviteToken = React.useRef(initialInviteToken.current);
  const [checkingInvite, setCheckingInvite] = React.useState(Boolean(initialInviteToken.current));
  const [retryAvailable, setRetryAvailable] = React.useState(false);
  const [creatingCollaborator, setCreatingCollaborator] = React.useState(false);
  const [inviteBlocked, setInviteBlocked] = React.useState<null | "expired" | "cancelled" | "used">(
    null,
  );
  const collaboratorName = React.useRef<string>("");
  const collaboratorPhone = React.useRef<string>("");
  const collaboratorPassword = React.useRef<string>("");
  const collaboratorConfirmPassword = React.useRef<string>("");
  const mounted = React.useRef(true);
  const inviteCheckController = React.useRef<AbortController | null>(null);
  const collaboratorController = React.useRef<AbortController | null>(null);
  const redirectTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const bandwidth = getBandwidthMbps();

  class Backend {
    static async checkInviteToken(rawToken: string) {
      const token = rawToken.trim();
      if (!token) return;

      retryInviteToken.current = token;
      inviteCheckController.current?.abort();
      collaboratorController.current?.abort();
      setInviteToken("");
      setOrgData(undefined);
      collaboratorName.current = "";
      collaboratorPhone.current = "";
      collaboratorPassword.current = "";
      collaboratorConfirmPassword.current = "";
      setCheckingInvite(true);
      setRetryAvailable(false);
      setBannerFeedbackIsOpen(false);
      const controller = new AbortController();
      inviteCheckController.current = controller;
      try {
        const res = await enterpriseAPI.post(
          "/invite/token",
          { token },
          {
            params: {
              bandwidth,
            },
            signal: controller.signal,
            timeout: INVITE_REQUEST_TIMEOUT_MS,
          },
        );
        if (!mounted.current || controller.signal.aborted) return;
        const data = res.data;
        // The Enterprise API projection with inviteId and isOwner deploys on its
        // own schedule. Until it is live the response carries neither field
        // (main's shape, representative data for every recipient): keep main's
        // behaviour there. Once the server asserts an identity it must bind to
        // this route and carry the scope, or the invitation fails closed.
        const legacyProjection = data?.inviteId === undefined && data?.isOwner === undefined;
        if (
          typeof data?.collaboratorEmail !== "string" ||
          typeof data?.orgName !== "string" ||
          (!legacyProjection && (data.inviteId !== inviteID || typeof data.isOwner !== "boolean"))
        ) {
          throw Object.assign(new Error("Invitation response does not match this route"), {
            status: 403,
          });
        }
        const representativeName =
          (legacyProjection || data.isOwner) && typeof data.representativeName === "string"
            ? data.representativeName
            : "";
        const invitation: OrgProps = {
          inviteId: inviteID,
          isOwner: legacyProjection ? representativeName !== "" : data.isOwner,
          collaboratorEmail: data.collaboratorEmail,
          orgName: data.orgName,
          orgAvatar: typeof data.orgAvatar === "string" ? data.orgAvatar : "",
          representativeName,
          representativePhone:
            (legacyProjection || data.isOwner) && typeof data.representativePhone === "string"
              ? data.representativePhone
              : "",
        };
        setInviteToken(token);
        setOrgData(invitation);
        collaboratorName.current = invitation.representativeName ?? "";
        collaboratorPhone.current = invitation.representativePhone ?? "";
        setBannerFeedbackIsOpen(false);
        setBannerFeedbackMessage("");
        retryInviteToken.current = "";
      } catch (err: any) {
        if (!mounted.current || controller.signal.aborted) return;
        const status = err?.response?.status ?? err?.status;
        if (status === 410) {
          const reason = err?.response?.data?.reasonCode;
          retryInviteToken.current = "";
          setInviteBlocked(reason === "cancelled" || reason === "used" ? reason : "expired");
          return;
        }
        setBannerFeedbackType("error");
        setBannerFeedbackIsOpen(true);
        if (status === 400 || status === 403 || status === 404) {
          retryInviteToken.current = "";
          setBannerFeedbackMessage(
            "Este link de convite não é válido. Abra o link completo do e-mail ou solicite um novo convite.",
          );
          return;
        }
        setRetryAvailable(true);
        setBannerFeedbackMessage(
          getUserFacingError(err, {
            context: "general",
            fallback:
              "Não foi possível verificar o convite agora. Ele continua salvo neste navegador; tente novamente.",
          }),
        );
      } finally {
        if (inviteCheckController.current === controller) {
          inviteCheckController.current = null;
          if (mounted.current) setCheckingInvite(false);
        }
      }
    }

    static async createCollaborator() {
      let errors = "";
      if (collaboratorName.current.length < 3) {
        errors += "O nome do colaborador deve ter no mínimo 3 caracteres.\n";
      }

      if (collaboratorPassword.current !== collaboratorConfirmPassword.current) {
        errors += "As senhas não coincidem.\n";
      }

      if (collaboratorPassword.current.length < 8) {
        errors += "A senha deve ter no mínimo 8 caracteres.\n";
      }

      const hasNumbers = /\d/;
      if (!hasNumbers.test(collaboratorPassword.current)) {
        errors += "A senha deve conter números.\n";
      }

      const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/;
      if (!hasSpecialChars.test(collaboratorPassword.current)) {
        errors += "A senha deve conter caracteres especiais.\n";
      }

      if (errors !== "") {
        setBannerFeedbackMessage(errors);
        setBannerFeedbackType("error");
        setBannerFeedbackIsOpen(true);
        return;
      }

      if (
        collaboratorController.current ||
        !orgData ||
        orgData.inviteId !== inviteID ||
        !inviteToken
      )
        return;
      setCreatingCollaborator(true);
      const controller = new AbortController();
      collaboratorController.current = controller;
      try {
        const response = await enterpriseAPI.post(
          "/create-collaborator",
          {
            inviteId: inviteID,
            inviteToken: inviteToken,
            collaboratorName: orgData.representativeName
              ? collaboratorName.current
              : collaboratorName.current.normalize("NFC"),
            collaboratorPassword: collaboratorPassword.current,
            collaboratorEmail: orgData?.collaboratorEmail,
            phone: collaboratorPhone.current,
          },
          { signal: controller.signal, timeout: INVITE_REQUEST_TIMEOUT_MS },
        );
        if (!mounted.current || controller.signal.aborted) return;
        const { jwt, refreshToken, permissions } = response.data ?? {};
        if (typeof jwt === "string" && typeof refreshToken === "string") {
          try {
            const accepted = await replaceEnterpriseAuthSession(
              { jwt, refreshToken, permissions },
              () => mounted.current && !controller.signal.aborted,
            );
            if (!accepted) return;
          } catch {
            if (!mounted.current || controller.signal.aborted) return;
            setBannerFeedbackType("success");
            setBannerFeedbackIsOpen(true);
            setBannerFeedbackMessage(
              "Conta criada. O navegador não conseguiu salvar sua sessão; faça login para continuar.",
            );
            redirectTimer.current = setTimeout(() => window.location.replace("/auth"), 2000);
            return;
          }
          if (!mounted.current || controller.signal.aborted) return;
          window.location.replace("/painel");
          return;
        }
        setBannerFeedbackType("success");
        setBannerFeedbackIsOpen(true);
        setBannerFeedbackMessage(
          "Conta criada. Não foi possível abrir sua sessão automaticamente; faça login para continuar.",
        );
        redirectTimer.current = setTimeout(() => {
          window.location.replace("/auth");
        }, 2000);
      } catch (err: any) {
        if (!mounted.current || controller.signal.aborted) return;
        const status = err?.response?.status ?? err?.status;
        if (status === 410) {
          const reason = err?.response?.data?.reasonCode;
          setInviteBlocked(reason === "cancelled" || reason === "used" ? reason : "expired");
          return;
        }
        setBannerFeedbackType("error");
        setBannerFeedbackIsOpen(true);
        if (status === 403) {
          setBannerFeedbackMessage(
            "Este link de convite não é válido. Abra o link completo do e-mail ou solicite um novo convite.",
          );
          return;
        }
        setBannerFeedbackMessage(
          getUserFacingError(err, {
            fallback: "Não foi possível concluir seu cadastro agora. Tente novamente em instantes.",
          }),
        );
      } finally {
        if (collaboratorController.current === controller) {
          collaboratorController.current = null;
        }
        if (mounted.current) setCreatingCollaborator(false);
      }
    }
  }

  React.useEffect(() => {
    mounted.current = true;
    const token = initialInviteToken.current;
    if (token) {
      cleanOwnerInviteLocation(window.location, window.history);
      void Backend.checkInviteToken(token);
    }
    return () => {
      mounted.current = false;
      inviteCheckController.current?.abort();
      collaboratorController.current?.abort();
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (inviteBlocked) {
    const copy = {
      expired: {
        title: "Convite expirado",
        body: "Este convite expirou. Não é possível concluir o cadastro por aqui. A equipe DaKasa será avisada e poderá enviar um novo convite, ou entrará em contato.",
      },
      cancelled: {
        title: "Convite cancelado",
        body: "Este convite foi cancelado. Em caso de dúvida, fale com a equipe DaKasa.",
      },
      used: {
        title: "Convite já utilizado",
        body: "Este convite já foi utilizado. Se a conta é sua, faça login.",
      },
    }[inviteBlocked];

    return (
      <div className="invitePageContainer dark-theme">
        <div className="boxTokenInput">
          <img
            className="inviteLogo"
            src={`${window.location.origin}/assets/Logo_DAKASA_V1_Branco.png`}
            alt="DaKasa LTDA"
          />
          <h1 className="inviteOrgName">{copy.title}</h1>
          <p className="inviteBlockedMessage">{copy.body}</p>
          {inviteBlocked === "used" && (
            <Button
              class="btnInviteConclude"
              icon={<Send />}
              iconSlot="end"
              callback={() => window.location.replace("/auth")}
            >
              Ir para o login
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (inviteToken === "") {
    return (
      <div className="invitePageContainer dark-theme">
        <div className="boxTokenInput">
          <img
            className="inviteLogo"
            src={`${window.location.origin}/assets/Logo_DAKASA_V1_Branco.png`}
            alt="DaKasa LTDA"
          />
          <BannerFeedback
            type={bannerFeedbackType}
            open={bannerFeedbackIsOpen}
            message={bannerFeedbackMessage}
            callback={() => setBannerFeedbackIsOpen(false)}
            sx={{
              marginTop: "var(--spacing-2xl)",
              width: "100%",
            }}
          />
          {checkingInvite && (
            <p className="inviteTokenStatus" role="status">
              Validando seu convite seguro…
            </p>
          )}
          {!checkingInvite && retryAvailable && (
            <Button
              class="btnInviteConclude"
              icon={<Send />}
              iconSlot="end"
              callback={() => void Backend.checkInviteToken(retryInviteToken.current)}
            >
              Tentar novamente
            </Button>
          )}
          {!checkingInvite && !retryAvailable && (
            <form
              className="tokenInputContainer"
              onSubmit={(event) => {
                event.preventDefault();
                void Backend.checkInviteToken(manualToken);
              }}
            >
              <input
                placeholder="Insira o token do convite"
                type="text"
                className="inputInviteToken"
                value={manualToken}
                onChange={(event) => setManualToken(event.target.value)}
              />
              <button className="btnInviteToken" type="submit" aria-label="Validar convite">
                <Send />
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="invitePageContainer dark-theme">
      <div className="boxTokenInput">
        <Avatar className="inviteOrgAvatar" src={orgData?.orgAvatar} alt="DaKasa LTDA" />
        <h1 className="inviteOrgName">{orgData?.orgName}</h1>
        <BannerFeedback
          type={bannerFeedbackType}
          open={bannerFeedbackIsOpen}
          message={bannerFeedbackMessage}
          callback={() => setBannerFeedbackIsOpen(false)}
          sx={{
            whiteSpace: "pre-wrap",
            maxWidth: "var(--width-md)",
            width: "100%",
          }}
        />
        {orgData?.representativeName ? (
          <input
            className="inputInviteFields"
            type="text"
            maxLength={25}
            value={orgData.representativeName}
            readOnly
            placeholder="Nome e sobrenome"
          />
        ) : (
          <input
            className="inputInviteFields"
            type="text"
            maxLength={25}
            defaultValue=""
            onInput={(e) => {
              collaboratorName.current = (e.target as HTMLInputElement).value;
            }}
            placeholder="Nome e sobrenome"
          />
        )}
        <input
          className="inputInviteFields"
          type="text"
          maxLength={20}
          defaultValue={orgData?.representativePhone ?? ""}
          onInput={(e) => {
            collaboratorPhone.current = (e.target as HTMLInputElement).value;
          }}
          placeholder="Telefone"
        />
        <input
          className="inputInviteFields"
          type="password"
          onInput={(e) => {
            collaboratorPassword.current = (e.target as HTMLInputElement).value;
          }}
          placeholder="Senha"
        />
        <input
          className="inputInviteFields"
          type="password"
          onInput={(e) => {
            collaboratorConfirmPassword.current = (e.target as HTMLInputElement).value;
          }}
          placeholder="Confirmar senha"
        />
        <Button
          class="btnInviteConclude"
          icon={<Send />}
          iconSlot="end"
          callback={() => Backend.createCollaborator()}
          loading={{ state: creatingCollaborator, text: "Criando sua conta…" }}
        >
          Concluir
        </Button>
      </div>
    </div>
  );
};
